import { DATE_FORMAT, DATE_FORMAT_FOR_BACKEND, MILLISECOND_IN_DAY } from 'Constants/Global'
import { decimalRounded, DecimalRoundedType, isEmptyValue } from 'Helpers'
import { RequestParams, ShortDataModel } from 'Models'
import { i18n, RadioOptionType } from 'LibNeo'
import { FrontUrls } from 'Constants/FrontUrls'
import moment from 'moment/moment'
import { Callback } from 'Src/types/utilTypes'
import { FieldNames } from 'Constants/FieldNames'
import {
	AMOUNT_COEFF,
	BankGuarantee,
	BankGuaranteeFull,
	BankGuaranteeKinds,
	BankGuaranteeSaveModel,
	BankGuaranteeTypes,
	BgStatus,
	CalcAgentCommission,
	COMMISSION_COEFF,
	ConditionsModel,
	ConditionsType,
	GetBaseAgentCommission,
	GuaranteeType,
	GuaranteeTypeTranslate,
	MAX_COUNT_DAYS_BG,
	MAX_COUNT_DAYS_WARRANTY_BG,
	MIN_AMOUNT,
	MIN_AMOUNT_NOVA,
	PRE_REQUEST_STATUS,
	PurchaseType,
	PurchaseTypeTranslate,
	SEGMENT_INCREASED,
	START_DATE_MAX_HOUR,
} from 'Pages/ibro'
import { MessageErrorType, VALIDATOR_ERROR_MESSAGE, Validators } from 'Modules'

export const dateDiff = (start, end): number => {
	if (start && end) {
		const diff = new Date(end).setHours(12) - new Date(start).setHours(12)
		return Math.round(diff / MILLISECOND_IN_DAY) || 0
	}
	return 0
}

export const disabledGuaranteeType = (
	guaranteeType: GuaranteeType,
	purchasetype: PurchaseType
): boolean => {
	switch (guaranteeType) {
		case GuaranteeType.REQUEST:
			return purchasetype && purchasetype === PurchaseType.PP615
		case GuaranteeType.WARRANTY:
			return purchasetype && purchasetype !== PurchaseType.FZ44
		case GuaranteeType.EXECUTION:
		default:
			return false
	}
}

export const calcIncreasedAmount = (amountBase: number): number =>
	decimalRounded((Number(`${amountBase}e${2}`) * AMOUNT_COEFF) / 100)

export const calcIncreasedCommission = (amountBase: number): number =>
	decimalRounded((Number(`${amountBase}e${2}`) * COMMISSION_COEFF) / 100, {
		type: DecimalRoundedType.TRUNC,
	})

export const isInitialParamsSocket = (params, item): boolean => {
	let flag = true
	Object.keys(params || {}).forEach((key): void => {
		const value = params[key]
		/* eslint-disable-next-line */
		if (value != item[key]) flag = false
	})
	return flag
}

const getGovernmentMaxCountDays = (
	segment: string,
	guaranteeType: GuaranteeType,
	isStatusNova: boolean
) => {
	if (
		(isStatusNova && !segment) ||
		segment === SEGMENT_INCREASED ||
		guaranteeType === GuaranteeType.WARRANTY
	)
		return MAX_COUNT_DAYS_WARRANTY_BG

	return MAX_COUNT_DAYS_BG
}

export const getMaxCountDays = (
	segment: string,
	guaranteeType: GuaranteeType,
	kind: BankGuaranteeKinds,
	isStatusNova: boolean,
	ptype: BankGuaranteeTypes
) => {
	if (ptype === BankGuaranteeTypes.COMMERCIAL_DIRECT) return MAX_COUNT_DAYS_BG

	if (kind === BankGuaranteeKinds.GOVERNMENT)
		return getGovernmentMaxCountDays(segment, guaranteeType, isStatusNova)

	if (segment !== SEGMENT_INCREASED && guaranteeType === GuaranteeType.WARRANTY)
		return MAX_COUNT_DAYS_WARRANTY_BG
	return MAX_COUNT_DAYS_BG
}

export const isIbroRequestsPage = () => window.location.pathname === FrontUrls.broker.requests

export const getDuplicatePostfix = (originalExternalId: string | null) =>
	originalExternalId ? '-D' : ''

export const mapShortDataToRadioOption = (data: ShortDataModel[] = []): RadioOptionType<string>[] =>
	data.map((item) => ({
		label: item.name,
		value: String(item.id),
		key: String(item.id),
		description: item.description,
	}))

export const getBaseAgentCommission = ({
	commission,
	calcDeliverySum,
	rewardPercentage,
}: GetBaseAgentCommission) => {
	if (rewardPercentage === null) return commission - calcDeliverySum
	return (commission - calcDeliverySum) * (rewardPercentage / 100)
}

export const calcAgentCommission = ({
	commission,
	calcDeliverySum,
	rewardPercentage,
	agentminfee,
	calcBaseCommission,
}: CalcAgentCommission) => {
	if (agentminfee && commission < calcBaseCommission) {
		const baseCommission = getBaseAgentCommission({
			commission: calcBaseCommission,
			rewardPercentage,
			calcDeliverySum,
		})
		return baseCommission - (calcBaseCommission - commission)
	}
	if (commission > calcBaseCommission) {
		const baseCommission = getBaseAgentCommission({
			commission: calcBaseCommission,
			rewardPercentage,
			calcDeliverySum,
		})

		const increasedCommission = getBaseAgentCommission({
			commission: commission - calcBaseCommission + calcDeliverySum,
			rewardPercentage: 70,
			calcDeliverySum,
		})

		return baseCommission + increasedCommission
	}
	return getBaseAgentCommission({
		commission,
		rewardPercentage,
		calcDeliverySum,
	})
}

export const isCommercialBg = (kind?: BankGuaranteeKinds) => kind === BankGuaranteeKinds.COMMERCIAL

export const isSbbolBgType = (type?: BankGuaranteeTypes) => type === BankGuaranteeTypes.SBBOL

export const isCommercialBgType = (type?: BankGuaranteeTypes) =>
	type === BankGuaranteeTypes.COMMERCIAL

export const isCommercialDirectBgType = (type?: BankGuaranteeTypes) =>
	type === BankGuaranteeTypes.COMMERCIAL_DIRECT

export const isCommercialFullyBg = (kind?: BankGuaranteeKinds) =>
	kind === BankGuaranteeKinds.COMMERCIAL

export const isCommercialFullyBgType = (type?: BankGuaranteeTypes) =>
	type === BankGuaranteeTypes.COMMERCIAL ||
	type === BankGuaranteeTypes.COMMERCIAL_DIRECT ||
	type === BankGuaranteeTypes.SBBOL

const getCurrentFormatMoscowDate = () => {
	const currentMoscowDate = new Date().toLocaleDateString('ru-Ru', {
		timeZone: 'Europe/Moscow',
	})
	return moment(currentMoscowDate, DATE_FORMAT).format('YYYY-MM-DD')
}

export const isStartDateMoreThanMaxHour = (date: string, maxHour: number): boolean => {
	const timeMoscowHours = new Date().toLocaleTimeString('ru-Ru', {
		timeZone: 'Europe/Moscow',
		hour: 'numeric',
	})
	const currentMoscowDate = getCurrentFormatMoscowDate()
	const formatDate = moment(date).format('YYYY-MM-DD')

	if (formatDate === currentMoscowDate) {
		return Number(timeMoscowHours) >= maxHour
	}
	return false
}

export const getNearestWorkingDay = (date: moment.Moment, isHoliday: Callback) => {
	if (!date || !isHoliday) return
	const current = moment(date, DATE_FORMAT_FOR_BACKEND)
	let count = 0
	while (count < 100) {
		const tomorrow = current.add(1, 'days')
		if (!isHoliday(tomorrow)) return tomorrow
		count += 1
	}
}

export const getStartDateInfoText = (
	date: moment.Moment,
	isHoliday: Callback<moment.Moment, boolean>
): string => {
	const timeMoscowHours = new Date().toLocaleTimeString('ru-Ru', {
		timeZone: 'Europe/Moscow',
		hour: 'numeric',
	})
	const currentMoscowDate = getCurrentFormatMoscowDate()
	const formatDate = moment(date).format('YYYY-MM-DD')

	if (formatDate === currentMoscowDate && Number(timeMoscowHours) < START_DATE_MAX_HOUR) {
		const nearestWorkingDay = getNearestWorkingDay(date, isHoliday)

		if (nearestWorkingDay === undefined) return ''

		return (
			`* По заявкам со сроком начала действия гарантии ${date.format(
				DATE_FORMAT
			)} клиенту необходимо до 14:00 МСК подписать заявку и оплатить комиссию за выпуск гарантии.\nВ` +
			` ином случае необходимо скорректировать ` +
			`дату начала гарантии на ${nearestWorkingDay.format(DATE_FORMAT)} и позже.`
		)
	}
	return ''
}

export const isFilledParams = (params: RequestParams) =>
	Object.keys(params.params).some(
		(paramName) =>
			!isEmptyValue(params[paramName]) &&
			(params[paramName] || paramName !== FieldNames.WITH_ARCHIVE)
	)

export const getFormValueForRequest = (
	values: Partial<BankGuaranteeFull>,
	item: BankGuaranteeFull
) => {
	values[FieldNames.SECONDACCOUNT] = values[FieldNames.SECONDACCOUNT] || ''
	values[FieldNames.THIRDACCOUNT] = values[FieldNames.THIRDACCOUNT] || ''
	if (!values[FieldNames.PERSONREZIDFLAG]) {
		values[FieldNames.TAXCOUNTRY1] = undefined
		values[FieldNames.TAXIN1] = undefined
		values[FieldNames.TAXREASON1] = undefined
		values[FieldNames.TAXREASONTEXT1] = undefined
		values[FieldNames.TAXCOUNTRY2] = undefined
		values[FieldNames.TAXIN2] = undefined
		values[FieldNames.TAXREASON2] = undefined
		values[FieldNames.TAXREASONTEXT2] = undefined
	}
	Object.keys(values).forEach((fieldName) => {
		if (item[fieldName] === undefined) values[fieldName] = undefined
	})
	values[FieldNames.ID] = item.id

	return values as BankGuaranteeSaveModel
}

export const getFieldsWidth = (fieldName: FieldNames): number => {
	switch (fieldName) {
		case FieldNames.NEWGUARANTEEBLANK:
		case FieldNames.BIGDEALPROTOCOL:
		case FieldNames.BIGDEALAGREEMENT:
			return 24
		default:
			return 12
	}
}

export const getAboutBgTitle = (item: BankGuarantee): string => {
	return `Банковская гарантия 
	${
		item.guaranteetype
			? `на ${GuaranteeTypeTranslate[item.guaranteetype?.guaranteetype_slug]?.toLowerCase()} `
			: ''
	}
	${item.purchasetype ? `по ${PurchaseTypeTranslate[item.purchasetype.purchasetype_slug]}` : ''}`
}

const checkCondition = (currentValue: any, type: ConditionsType, value: any): boolean => {
	switch (type) {
		case ConditionsType.EQ:
			return currentValue === value
		default:
			return currentValue === value
	}
}
export const checkConditions = (item: BankGuarantee, conditions: ConditionsModel[]): boolean => {
	for (let i = 0; i < conditions.length; i += 1) {
		if (!checkCondition(item[conditions[i].field], conditions[i].type, conditions[i].value))
			return false
	}
	return true
}

export const getNotAllowedFields = <T>(item: T) => {
	return Object.keys(item).filter((fieldName) => item[fieldName] === undefined) as FieldNames[]
}

export const ptypeNameTranslateById = (ptypes: ShortDataModel[], ptype: BankGuaranteeTypes) =>
	ptypes.find((item) => item.id === ptype)?.name ?? ''

export const amountCustomValidators = (
	item: BankGuaranteeFull,
	maxAmountForValidate?: number,
	messageError?: MessageErrorType,
	commissionReject?: boolean
) => {
	const {
		required_fields = [],
		status: { status_slug: status },
		segment,
		ptype,
	} = item
	const purchaseSlug = item?.purchasetype?.purchasetype_slug
	const guaranteetypeSlug = item?.guaranteetype?.guaranteetype_slug
	if (ptype === BankGuaranteeTypes.COMMERCIAL_DIRECT && status === BgStatus.NOVA) {
		return [
			{
				validator: Validators.checkPositiveNumber({
					required: PRE_REQUEST_STATUS.includes(status),
					payload: {
						min: MIN_AMOUNT_NOVA,
					},
					messageError: { min: i18n('broker.validate_max_amount_nova') },
				}),
			},
		]
	}
	if (
		purchaseSlug === PurchaseType.FZ44 &&
		guaranteetypeSlug === GuaranteeType.EXECUTION &&
		segment === SEGMENT_INCREASED &&
		status === BgStatus.CL_CHECKED
	) {
		return [
			{
				validator: Validators.checkPositiveNumber({
					required:
						PRE_REQUEST_STATUS.includes(status) || required_fields.includes(FieldNames.AMOUNT),
					payload: {
						min: MIN_AMOUNT,
						max: maxAmountForValidate,
					},
					messageError: { [VALIDATOR_ERROR_MESSAGE.MAX]: i18n('broker.validate_max_amount_ast') },
				}),
			},
			{
				validator: Validators.checkCommissionReject({
					required:
						PRE_REQUEST_STATUS.includes(status) || required_fields.includes(FieldNames.AMOUNT),
					payload: {
						commissionReject: Boolean(commissionReject),
					},
				}),
			},
		]
	}
	if (status !== BgStatus.NOVA && segment === SEGMENT_INCREASED) {
		return [
			{
				validator: Validators.checkPositiveNumber({
					required:
						PRE_REQUEST_STATUS.includes(status) || required_fields.includes(FieldNames.AMOUNT),
					payload: {
						min: MIN_AMOUNT,
						max: maxAmountForValidate,
					},
					messageError,
				}),
			},
			{
				validator: Validators.checkCommissionReject({
					required:
						PRE_REQUEST_STATUS.includes(status) || required_fields.includes(FieldNames.AMOUNT),
					payload: {
						commissionReject: Boolean(commissionReject),
					},
				}),
			},
		]
	}

	return [
		{
			validator: Validators.checkRequired({
				required:
					PRE_REQUEST_STATUS.includes(status) || required_fields.includes(FieldNames.AMOUNT),
			}),
		},
	]
}
